import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ArrowLeftIcon,
  AwardIcon,
  LayersIcon,
  ZapIcon
} from 'lucide-react';
import learningLevelsData from '../../data/learningLevels.json';
import api from '../../services/api';

const FlashAnzanLevelStatistics = () => {
  const { t } = useTranslation();
  const { currentLanguage, isRTL } = useLanguage();
  const [flashAnzanLevelId, setFlashAnzanLevelId] = useState(1);
  const [flashAnzanData, setFlashAnzanData] = useState(null);
  const [flashAnzanLoading, setFlashAnzanLoading] = useState(false);

  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  useEffect(() => {
    const fetchFlashAnzanStats = async () => {
      setFlashAnzanLoading(true);
      try {
        const res = await api.get(`/education/flash-anzan/statistics/comprehensive`, {
          params: { levelId: flashAnzanLevelId }
        });
        if (res.data.success) {
          setFlashAnzanData(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching flash anzan statistics:', err);
      } finally {
        setFlashAnzanLoading(false);
      }
    };
    fetchFlashAnzanStats();
  }, [flashAnzanLevelId]);

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeftIcon className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            {t('dashboard.stats.backToDashboard')}
          </Link>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ZapIcon className="w-7 h-7 text-blue-600" />
            {t('dashboard.stats.flashAnzanLevelTitle')}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{t('dashboard.stats.flashAnzanLevelSubtitle')}</p>
        </div>

        <section className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 via-cyan-50 to-teal-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <ZapIcon className="w-5 h-5 text-blue-600" />
              {t('dashboard.stats.flashAnzanStats')}
            </h2>
            <div className="flex items-center gap-2">
              <LayersIcon className="w-4 h-4 text-slate-500" />
              <select
                value={flashAnzanLevelId}
                onChange={(e) => setFlashAnzanLevelId(Number(e.target.value))}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:border-blue-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
              >
                {(flashAnzanData?.availableLevels?.length
                  ? flashAnzanData.availableLevels
                  : [{ id: flashAnzanLevelId, name: '' }]
                ).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name ? (translateLevelName(l.name) || l.name) : t('dashboard.stats.selectLevel')}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="p-6">
            {flashAnzanLoading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent" />
              </div>
            ) : flashAnzanData?.summary ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                  {[
                    { key: 'totalStudents', label: t('dashboard.stats.totalStudents'), value: flashAnzanData.summary.totalStudents ?? 0, from: 'from-slate-500', to: 'to-slate-600' },
                    { key: 'activeStudents', label: t('dashboard.stats.activeStudents'), value: flashAnzanData.summary.activeStudents ?? 0, from: 'from-emerald-500', to: 'to-emerald-600' },
                    { key: 'inactiveStudents', label: t('dashboard.stats.inactiveStudents'), value: flashAnzanData.summary.inactiveStudents ?? 0, from: 'from-gray-500', to: 'to-gray-600' },
                    { key: 'totalFlashAnzanSessions', label: t('dashboard.stats.totalFlashAnzanSessions'), value: flashAnzanData.summary.totalFlashAnzanSessions ?? 0, from: 'from-blue-500', to: 'to-blue-600' },
                    { key: 'correctFlashAnzanSessions', label: t('dashboard.stats.correctFlashAnzanSessions'), value: flashAnzanData.summary.correctFlashAnzanSessions ?? 0, from: 'from-violet-500', to: 'to-violet-600' },
                    { key: 'correctPercentage', label: t('dashboard.stats.correctPercentage'), value: `${flashAnzanData.summary.correctPercentage ?? 0}%`, from: 'from-amber-500', to: 'to-amber-600' }
                  ].map((item) => (
                    <div
                      key={item.key}
                      className={`rounded-xl bg-gradient-to-br ${item.from} ${item.to} p-4 text-white shadow-md`}
                    >
                      <p className="text-xs font-medium opacity-90 truncate">{item.label}</p>
                      <p className="text-2xl font-bold mt-1">{item.value}</p>
                    </div>
                  ))}
                </div>
                {flashAnzanData.topStudents && flashAnzanData.topStudents.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <AwardIcon className="w-4 h-4 text-amber-500" />
                      {t('dashboard.stats.topPerformers')}
                    </h3>
                    <div className="space-y-2">
                      {flashAnzanData.topStudents.map((s) => (
                        <div
                          key={s.userId}
                          className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-50 to-white hover:from-blue-50 hover:to-white border border-slate-100 hover:border-blue-200 transition-all"
                        >
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm ${
                              s.rank === 1 ? 'bg-amber-500' :
                              s.rank === 2 ? 'bg-slate-400' :
                              s.rank === 3 ? 'bg-amber-700' : 'bg-slate-300'
                            }`}
                          >
                            {s.rank}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900 truncate">
                              {s.firstName} {s.lastName}
                            </p>
                            <p className="text-xs text-slate-500">{s.studentNumber}</p>
                          </div>
                          <div className="text-end">
                            <p className="font-bold text-blue-600">
                              {parseFloat(s.totalScore || 0).toFixed(1)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {s.correctSessions}/{s.totalSessions} {t('dashboard.stats.correct')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-slate-500 text-center py-12">{t('dashboard.stats.noData')}</p>
            )}
          </div>
        </section>
    </div>
  );
};

export default FlashAnzanLevelStatistics;
